import { AlertService, AlertRule, AlertDestination } from '../../services/alert.ts';
import { DomainService, Domain } from '../../services/domain.ts';
import { ToastService } from '../../services/toast.ts';
import { getAlertEditorStyles } from './alert-editor-styles.ts';

import '../library/ui/app-modal.ts';
import '../library/ui/app-button.ts';
import '../library/ui/app-icon.ts';
import '../library/form/form-input.ts';
import '../library/form/form-select.ts';
import '../library/form/form-toggle.ts';

export class AlertEditor extends HTMLElement {
    private _alertService = AlertService.getInstance();
    private _domainService = DomainService.getInstance();
    private _toastService = ToastService.getInstance();

    private _orgId: string = '';
    private _ruleId: string | null = null;
    private _rule: AlertRule | null = null;
    private _domains: Domain[] = [];
    private _isLoading = false;
    private _isSaving = false;
    private _isInitialized = false;
    private _hasEventListeners = false;

    // Form State
    private _name: string = '';
    private _metricType: string = 'error_count_5xx';
    private _domainId: string = '';
    private _threshold: number = 10;
    private _timeWindow: number = 5;
    private _cooldown: number = 30;
    private _enabled: boolean = true;
    private _destinations: AlertDestination[] = [];

    static get observedAttributes() {
        return ['org-id', 'rule-id'];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    attributeChangedCallback(name: string, oldVal: string, newVal: string) {
        if (oldVal === newVal) return;
        if (name === 'org-id') this._orgId = newVal;
        if (name === 'rule-id') this._ruleId = newVal === 'new' ? null : newVal;
        
        if (this._orgId && this.isConnected) {
            this.init();
        }
    }

    async connectedCallback() {
        if (!this._isInitialized) {
            this.render();
        }
        if (this._orgId) {
            this.init();
        }
    }

    private async init() {
        this._isLoading = true;
        this.render();

        try {
            const [domains, rule] = await Promise.all([
                this._domainService.listByOrganization(this._orgId),
                this._ruleId ? this._alertService.get(this._ruleId) : Promise.resolve(null)
            ]);

            this._domains = domains || [];
            
            if (rule) {
                this._rule = rule;
                this._name = rule.name;
                this._metricType = rule.metric_type;
                this._domainId = rule.domain_id;
                this._threshold = rule.threshold;
                this._timeWindow = rule.time_window;
                this._cooldown = rule.cooldown_minutes || 30;
                this._enabled = rule.enabled;
                this._destinations = [...(rule.destinations || [])];
            } else {
                this._rule = null;
                this._name = '';
                this._domainId = this._domains.length > 0 ? this._domains[0].id! : '';
                this._destinations = [{ type: 'email', target: '' }];
            }
        } catch (e: any) {
            // Toast handled by ApiClient
        } finally {
            this._isLoading = false;
            this.render();
        }
    }

    private async handleSave() {
        if (this._isSaving) return;
        
        if (!this._name) {
            this._toastService.warning('Please enter a name for the alert rule');
            return;
        }

        this._isSaving = true;
        this.renderActions();

        try {
            const ruleData: AlertRule = {
                name: this._name,
                metric_type: this._metricType,
                domain_id: this._domainId,
                threshold: this._threshold,
                time_window: this._timeWindow,
                cooldown_minutes: this._cooldown,
                enabled: this._enabled,
                destinations: this._destinations.filter(d => d.target && d.target.trim() !== ''),
                organization_id: this._orgId
            };

            if (this._ruleId) {
                await this._alertService.update(this._ruleId, ruleData, this._orgId);
            } else {
                await this._alertService.create(ruleData, this._orgId);
            }

            this.dispatchEvent(new CustomEvent('save'));
            this.close();
        } catch (e: any) {
            console.error('[AlertEditor] Save failed:', e);
            // Toast handled by ApiClient
        } finally {
            this._isSaving = false;
            this.renderActions();
        }
    }

    private async handleDelete() {
        if (!this._ruleId || !confirm('Are you sure you want to delete this alert rule?')) return;

        try {
            await this._alertService.delete(this._ruleId);
            this.dispatchEvent(new CustomEvent('save'));
            this.close();
        } catch (e: any) {
            // Toast handled by ApiClient
        }
    }

    private close() {
        this.dispatchEvent(new CustomEvent('close'));
    }

    private addDestination() {
        this._destinations.push({ type: 'email', target: '' });
        this.renderDestinations();
    }

    private removeDestination(index: number) {
        this._destinations.splice(index, 1);
        this.renderDestinations();
    }

    render() {
        if (!this._isInitialized) {
            this.shadowRoot!.innerHTML = `
                <style>${getAlertEditorStyles()}</style>
                <app-modal open size="lg" id="alert-modal">
                    <div slot="header" id="header-slot"></div>
                    <div id="body-slot"></div>
                    <div slot="actions" id="actions-slot"></div>
                </app-modal>
            `;
            this._isInitialized = true;
            this.setupEventListeners();
        }

        if (this._isLoading) {
            this.renderHeader(true);
            const body = this.shadowRoot!.getElementById('body-slot');
            if (body) {
                body.innerHTML = `
                    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; color: var(--text-dim); font-family: var(--font-mono); letter-spacing: 0.1em; font-size: 0.8rem;">
                        <app-icon name="loader" spin style="margin-bottom: 1rem;"></app-icon>
                        SYNCHRONIZING_ALERT_ENGINE...
                    </div>
                `;
            }
            this.renderActions();
            return;
        }

        this.renderHeader();
        this.renderBody();
        this.renderActions();
    }

    private renderHeader(isLoading = false) {
        const header = this.shadowRoot!.getElementById('header-slot');
        if (!header) return;

        if (isLoading) {
            header.innerHTML = `<div style="font-family: var(--font-sketch); font-size: 1.5rem; opacity: 0.5;">Initializing...</div>`;
            return;
        }

        header.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                <span style="font-family: var(--font-sketch); font-size: 1.5rem;">${this._ruleId ? 'Edit Alert' : 'New Alert'}</span>
                <div style="display: flex; align-items: center; gap: 0.75rem; margin-right: 1.5rem;">
                    <label style="font-size: 0.7rem; color: var(--text-dim); text-transform: uppercase; font-weight: 700; letter-spacing: 0.1em;">Active</label>
                    <form-toggle id="enabled-toggle" name="enabled" ${this._enabled ? 'checked' : ''}></form-toggle>
                </div>
            </div>
        `;
    }

    private renderBody() {
        const body = this.shadowRoot!.getElementById('body-slot');
        if (!body) return;

        const metricOptions = [
            { id: 'error_rate_5xx', name: '5xx Error Rate (%)' },
            { id: 'error_count_5xx', name: '5xx Error Count' },
            { id: 'error_rate_4xx', name: '4xx Error Rate (%)' },
            { id: 'error_count_4xx', name: '4xx Error Count' },
            { id: 'error_rate_spike_5xx', name: '5xx Error Rate Spike (%)' },
            { id: 'error_rate_spike_4xx', name: '4xx Error Rate Spike (%)' },
            { id: 'traffic_drop', name: 'Traffic Drop (%)' },
            { id: 'traffic_spike', name: 'Traffic Spike (%)' }
        ];
        const domainOptions = this._domains.map(d => ({ id: d.id!, name: d.host }));

        body.innerHTML = `
            <div class="content">
                <div class="form-section">
                    <div class="section-label">Identity</div>
                    <form-input id="rule-name" name="name" label="Rule Name" value="${this._name}" placeholder="e.g. Production 5xx Spike"></form-input>
                    
                    <div class="form-grid">
                        <form-select id="domain-id" name="domain_id" label="Monitor Domain" 
                            options='${JSON.stringify(domainOptions.map(d => ({ value: d.id, label: d.name })))}'
                            value="${this._domainId}"></form-select>
                        <form-select id="metric-type" name="metric_type" label="Metric Type"
                            options='${JSON.stringify(metricOptions.map(m => ({ value: m.id, label: m.name })))}'
                            value="${this._metricType}"></form-select>
                    </div>
                </div>

                <div class="form-section">
                    <div class="section-label">Conditions</div>
                    <div class="form-grid">
                        <form-input type="number" id="threshold" name="threshold" label="Threshold" value="${this._threshold}"></form-input>
                        <form-input type="number" id="time-window" name="time_window" label="Time Window (Minutes)" value="${this._timeWindow}"></form-input>
                    </div>
                    
                    <div style="width: calc(50% - 12px);">
                        <form-input type="number" id="cooldown" name="cooldown" label="Cooldown (Minutes)" value="${this._cooldown}"></form-input>
                    </div>

                    <div class="metric-preview">
                        <div class="preview-summary"></div>
                    </div>
                </div>

                <div class="form-section">
                    <div class="section-label">Destinations</div>
                    <div class="destination-list" id="destination-list"></div>
                    <app-button variant="ghost" size="sm" icon="plus" style="margin-top: 12px;" id="add-destination-btn">Add Destination</app-button>
                </div>
            </div>
        `;

        this.updatePreviewSummary();
        this.renderDestinations();
    }

    private renderDestinations() {
        const list = this.shadowRoot!.getElementById('destination-list');
        if (!list) return;

        list.innerHTML = this._destinations.map((d, i) => `
            <div class="destination-item">
                <form-select class="dest-type" data-index="${i}" label="Type"
                    options='[{"value":"email","label":"Email"}]'
                    value="${d.type}"></form-select>
                <form-input class="dest-target" id="target-${i}" data-index="${i}" label="Target" value="${d.target}" placeholder="Recipient email address"></form-input>
                <app-button variant="ghost" icon="trash" size="sm" class="remove-dest-btn" data-index="${i}" style="margin-bottom: 1.5rem;"></app-button>
            </div>
        `).join('');
    }

    private renderActions() {
        const actions = this.shadowRoot!.getElementById('actions-slot');
        if (!actions) return;

        actions.innerHTML = `
            <div style="display: flex; width: 100%; gap: 12px; align-items: center;">
                ${this._ruleId ? `
                    <app-button variant="ghost" color="#ef4444" id="delete-btn" icon="trash" data-tooltip="Delete Rule" style="margin-right: auto;"></app-button>
                ` : ''}
                <div style="flex: 1; display: flex; justify-content: flex-end; gap: 12px;">
                    <app-button variant="ghost" id="cancel-btn" icon="x" data-tooltip="Cancel"></app-button>
                    <app-button variant="primary" id="save-btn" icon="check" data-tooltip="${this._ruleId ? 'Update Rule' : 'Create Rule'}" ${this._isSaving ? 'loading' : ''}></app-button>
                </div>
            </div>
        `;
    }

    private setupEventListeners() {
        if (this._hasEventListeners) return;
        const root = this.shadowRoot!;
        
        root.addEventListener('click', (e: Event) => {
            const path = e.composedPath();
            
            const findInPath = (selector: string) => {
                return path.find(el => el instanceof HTMLElement && el.matches(selector)) as HTMLElement | undefined;
            };

            if (findInPath('#cancel-btn')) {
                this.close();
            } else if (findInPath('#save-btn')) {
                this.handleSave();
            } else if (findInPath('#delete-btn')) {
                this.handleDelete();
            } else if (findInPath('#add-destination-btn')) {
                this.addDestination();
            } else {
                const removeBtn = findInPath('.remove-dest-btn');
                if (removeBtn) {
                    const index = parseInt(removeBtn.getAttribute('data-index') || '0');
                    this.removeDestination(index);
                }
            }
        });

        root.addEventListener('form-input', (e: any) => {
            const path = e.composedPath();
            const target = path[0] as HTMLElement;
            const id = target.id;

            if (!id) return;

            if (id === 'rule-name') this._name = e.detail.value;
            else if (id === 'threshold') {
                this._threshold = parseFloat(e.detail.value) || 0;
                this.updatePreviewSummary();
            }
            else if (id === 'time-window') {
                this._timeWindow = parseInt(e.detail.value) || 1;
                this.updatePreviewSummary();
            }
            else if (id === 'cooldown') this._cooldown = parseInt(e.detail.value) || 0;
            else if (id.startsWith('target-')) {
                const index = parseInt(id.split('-')[1]);
                this._destinations[index].target = e.detail.value;
            }
        });

        root.addEventListener('change', (e: any) => {
            const path = e.composedPath();
            const target = path[0] as HTMLElement;
            const id = target.id;

            if (!id) return;

            if (id === 'metric-type') {
                this._metricType = e.detail.value;
                this.updatePreviewSummary();
            }
            else if (id === 'domain-id') {
                this._domainId = e.detail.value;
                this.updatePreviewSummary();
            }
            else if (id === 'enabled-toggle') this._enabled = e.detail.checked;

            if (target.classList.contains('dest-type')) {
                const idx = parseInt(target.getAttribute('data-index')!);
                this._destinations[idx].type = e.detail.value;
            }
        });

        root.getElementById('alert-modal')?.addEventListener('close', () => this.close());
        this._hasEventListeners = true;
    }

    private updatePreviewSummary() {
        const metricOptions = [
            { id: 'error_count_5xx', name: '5xx Error Count' },
            { id: 'error_rate_5xx', name: '5xx Error Rate (%)' },
            { id: 'latency_p95', name: 'Latency P95 (ms)' },
            { id: 'traffic_drop', name: 'Traffic Drop (%)' },
            { id: 'traffic_spike', name: 'Traffic Spike (%)' }
        ];
        const domainOptions = this._domains.map(d => ({ id: d.id!, name: d.host }));
        
        const summary = this.shadowRoot!.querySelector('.preview-summary');
        if (summary) {
            const selectedMetric = metricOptions.find(m => m.id === this._metricType)?.name || 'selected metric';
            const selectedDomain = domainOptions.find(d => d.id === this._domainId)?.name || 'selected domain';
            summary.innerHTML = `
                Trigger alert if <b>${selectedMetric}</b> 
                on <b>${selectedDomain}</b> 
                exceeds <b>${this._threshold}</b> over a <b>${this._timeWindow}</b> minute window.
            `;
        }
    }
}

if (!customElements.get('app-alert-editor')) {
    customElements.define('app-alert-editor', AlertEditor);
}
